import { format } from 'date-fns';

import { Text } from '@/components/ui/text';
import { VStack } from '@/components/ui/vstack';
import type { ReferencedInquiry } from '@/services/conversations/referencedInquiry';
import { getProfileNameAndInitials } from '@/util/getProfileNameAndInitials';

const STATE_LABELS = { active: 'Open', closed: 'Closed' } as const;

/**
 * Who the inquiry is with, from the member's side: the expert they asked, or
 * the person who asked them. An anonymous submitter is never named, and an
 * inquiry with no expert yet says so rather than naming a routing account.
 */
export function referencedInquiryParticipant(inquiry: ReferencedInquiry): string {
  const name = inquiry.counterpart ? getProfileNameAndInitials(inquiry.counterpart).name : '';

  if (inquiry.role === 'recipient') {
    return name ? `From ${name}` : 'From an anonymous member';
  }

  if (inquiry.role === 'submitter') {
    return name ? `To ${name}` : 'No expert assigned yet';
  }

  return name || 'Linked inquiry';
}

function dateLabel(createdAt: string | null | undefined): string {
  if (!createdAt) {
    return '';
  }

  const date = new Date(createdAt);

  return Number.isNaN(date.getTime()) ? '' : format(date, 'MMM d, yyyy');
}

/**
 * The details line. Open or closed is shown only where it is read live, in the
 * attach list; a message's card is built from details stored when it was sent,
 * and a stored state would go stale the moment the inquiry changed.
 */
export function referencedInquiryDetails(
  inquiry: ReferencedInquiry,
  { showState = true }: { showState?: boolean } = {}
): string {
  return [
    inquiry.cancerType?.shortLabel || inquiry.cancerType?.label,
    dateLabel(inquiry.createdAt),
    showState && inquiry.state ? STATE_LABELS[inquiry.state] : '',
  ]
    .filter(Boolean)
    .join(' · ');
}

/** One line naming the inquiry for a screen reader, enough to tell two apart. */
export function referencedInquiryLabel(
  inquiry: ReferencedInquiry,
  options: { showState?: boolean } = {}
): string {
  return [
    referencedInquiryParticipant(inquiry),
    inquiry.question?.slice(0, 60),
    referencedInquiryDetails(inquiry, options),
  ]
    .filter(Boolean)
    .join(', ');
}

/**
 * How the support thread names an inquiry, in the attach list and on a
 * message's card alike, so staff see the same thing where they pick it and
 * where it lands. It leads with the other participant and the opening
 * question, not the inquiry title, whose age and diagnosis are often blank and
 * would make a member's inquiries read alike. The cancer type rides in the
 * details line when the inquiry has one.
 */
export function ReferencedInquirySummary({
  inquiry,
  showState = true,
}: {
  inquiry: ReferencedInquiry;
  showState?: boolean;
}) {
  const details = referencedInquiryDetails(inquiry, { showState });

  return (
    <VStack className="min-w-0 gap-0.5">
      <Text className="font-semibold text-sm text-typography-900" numberOfLines={1}>
        {referencedInquiryParticipant(inquiry)}
      </Text>
      {inquiry.question ? (
        <Text className="text-sm text-typography-700" numberOfLines={2}>
          {inquiry.question}
        </Text>
      ) : null}
      {details ? <Text className="text-xs text-typography-700">{details}</Text> : null}
    </VStack>
  );
}
